import React, { Suspense } from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { useLocation } from 'react-router-dom';
import { AppRoutes } from './router';
import { AuthProvider } from './auth/AuthProvider';
import { NotificationProvider } from './contexts/NotificationContext';
import { GlobalSearchProvider } from './contexts/GlobalSearchContext';
import { GlobalSearchModal } from './components/GlobalSearchModal';
import { RTLProvider } from './theme/RTLProvider';
import { ForgeProvider } from '@design2code/forge-ui';
import { ForgeFloatingButton } from '@design2code/forge-ui';
import { PSCWidget } from '@design2code/psc-widget';
import '@design2code/psc-widget/styles';

class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }
  override componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('Application error:', error, info);
  }
  override render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', p: 3, textAlign: 'center', bgcolor: '#FAFAF7', color: '#0E1116' }}>
          <h1>Something went wrong</h1>
          <p style={{ color: '#4A5568', maxWidth: 500 }}>An unexpected error occurred. Please refresh the page.</p>
          <button onClick={() => window.location.reload()} style={{ marginTop: 16, padding: '8px 16px', backgroundColor: '#E3E7EC', color: '#0B2545', border: '1px solid #0B2545', borderRadius: 4, cursor: 'pointer' }}>
            Refresh Page
          </button>
        </Box>
      );
    }
    return this.props.children;
  }
}

/**
 * InternalDevTools — FORGE + PSC devtools, hidden on /client/* routes.
 * Must be rendered inside BrowserRouter (App is already inside BrowserRouter in main.tsx).
 * QA finding #9: FORGE widget was visible to client-portal users.
 */
const InternalDevTools: React.FC = () => {
  const location = useLocation();
  if (location.pathname.startsWith('/client')) return null;
  return (
    <>
      <ForgeFloatingButton position="bottom-right" serviceId="jylaw" />
      <PSCWidget config={{ appName: 'JY Law', apiBaseUrl: '/api/v1/forge', position: 'bottom-left' }} />
    </>
  );
};

// Per-route ErrorBoundary — remounts on pathname change so a crash on one
// route does not pin the fallback for every subsequent navigation.
// (BL: the WikiIndexPage crash made /admin, /compliance, /conflict appear
// broken in the same SPA session until the user hit Refresh.)
const RouteErrorBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  return <ErrorBoundary key={location.pathname}>{children}</ErrorBoundary>;
};

const App: React.FC = () => (
  <RTLProvider>
    <CssBaseline />
    <NotificationProvider>
      <AuthProvider>
        <GlobalSearchProvider>
          <ForgeProvider serviceId="jylaw" forgeApiUrl="/api/v1/forge">
            <Suspense fallback={<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', bgcolor: '#FAFAF7' }}><CircularProgress sx={{ color: '#0B2545' }} /></Box>}>
              <RouteErrorBoundary>
                <AppRoutes />
              </RouteErrorBoundary>
            </Suspense>
            {/* GlobalSearchModal — mounted once at app root; Cmd+K opens it from anywhere */}
            <GlobalSearchModal />
            {/* InternalDevTools hides FORGE on /client/* (QA finding #9) */}
            <InternalDevTools />
          </ForgeProvider>
        </GlobalSearchProvider>
      </AuthProvider>
    </NotificationProvider>
  </RTLProvider>
);

export default App;
